<template>
	<Dropdown
		:options="dropdownOptions"
		v-model="filter"
		:icon="FilterIcon"
		optionsClass="org-user-filter-options"
		childOptionsClass="org-user-filter-child-options"
		class="org-user-filter-dropdown"
		@change="updateFilter"
		global
	>
		<template #selected="{ option }">
			<!-- {{ $t('contacts_userpicker_filter_title') }} -->
			<span class="selected-filter" v-if="option" v-text="option.label" />
		</template>
		<template v-slot="{ option }">
			<div class="filter-option" :class="{ disabled: option.disabled }">
				<div class="option-title" v-text="option.label" />
				<div class="option-desc" v-text="option.desc" />
			</div>
		</template>
		<template #child-option="{ option }">
			<div
				class="filter-group-option"
				:class="{ category: option.type === 'category' }"
				v-text="option.label"
			/>
		</template>
	</Dropdown>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Dropdown } from '@/components/common'
import { FilterIcon } from '@/icons'
import { orgCan, SEARCH_ORG } from '@/helpers/permissions'
import { useI18n } from 'vue-i18n'
import {
	getChatsWithNoCategory,
	getCategoriesWithChats,
	getMyUser,
} from '@/store'
import { useEventBus } from '@/helpers/bus'

const { t } = useI18n()
const emit = defineEmits(['change'])

const me = getMyUser()
const dropdownOptions = ref(getDropdownOptions())
const filter = ref(me.isAdmin ? 'all' : 'connections')

const updateFilter = () => {
	emit('change', filter.value)
}

onMounted(() => {
	dropdownOptions.value = getDropdownOptions()
})

useEventBus('i18n-loaded', () => {
	// in case this component gets mounted before i18n is loaded,
	// we need to regenerate the options so the translations are correct
	dropdownOptions.value = getDropdownOptions()
})

function getDropdownOptions() {
	const canSearchOrg = orgCan(SEARCH_ORG)
	const categories = getCategoriesWithChats()
	return [
		{
			id: 'all',
			label: t('contacts_filterpicker_organization_title'),
			desc: t('contacts_filterpicker_organization_description'),
			disabled: !canSearchOrg,
			noHover: !canSearchOrg,
		},
		{
			id: 'connections',
			label: t('contacts_filterpicker_connections_title'),
			desc: t('contacts_filterpicker_connections_description'),
		},
		{
			id: 'group',
			label: t('contacts_filterpicker_group_title'),
			desc: t('contacts_filterpicker_group_description'),
			childOptions: categories
				.flatMap(({ id, displayname, chats }) => [
					{ id, label: displayname, type: 'category', noHover: true },
					...chats.map((chat) => ({
						id: chat.id,
						label: chat.displayname,
						type: 'chat',
					})),
				])
				.concat([
					{
						id: 'none',
						label: t('chat_drawer_section_bubbles_uncatagorized'),
						type: 'category',
						noHover: true,
					},
					...getChatsWithNoCategory().map((chat) => ({
						id: chat.id,
						label: chat.displayname,
						type: 'chat',
					})),
				]),
		},
	].filter((x) => x)
}
</script>

<style lang="scss">
@import 'src/sass/variables';

.org-user-filter-options {
	width: 280px;
	.option-desc {
		font-size: 12px;
		color: var(--neutral-500);
	}
	.dropdown-options {
		.dropdown-option {
			@include flex-spread;
			.filter-option {
				&.disabled {
					cursor: not-allowed;
					.option-title,
					.option-desc {
						color: var(--neutral-500) !important;
					}
				}
			}
		}
	}
}

.org-user-filter-child-options {
	width: 280px !important;

	.dropdown-options {
		&.child-options {
			padding: 0;
			.child-option {
				margin: 0 8px;
				&.no-hover {
					padding: 0;
					margin: 0;
				}
			}
		}
	}
	.filter-group-option {
		width: 100%;
		@include ellipsis;
		&.category {
			text-transform: uppercase;
			font-size: 12px;
			letter-spacing: 1.4px;
			color: var(--neutral-600);
			background: var(--neutral-100);
			padding: 4px 16px;
			margin: 4px 0;
		}
	}
}

.org-user-filter-dropdown {
	width: 215px;
	flex-shrink: 0;
	.dropdown-selected {
		border: 1px solid var(--neutral-200) !important;
		background: var(--neutral-100);
	}
	.selected-filter {
		color: var(--blue-300);
	}
}

@include overrideScheme('dark') {
	.org-user-filter-dropdown {
		.dropdown-selected {
			background: var(--neutral-100) !important;
		}
	}
}
</style>
